import { SendbirdErrorCode } from "@sendbird/chat";

/**
 * Sendbird error codes that represent expected, transient connectivity
 * conditions: the device is offline, the SDK's websocket dropped, a request was
 * cancelled by a reconnect, or a request/ack timed out. These resolve on their
 * own once the connection recovers and the user retries, so they are not
 * actionable application defects.
 */
const EXPECTED_NETWORK_ERROR_CODES = new Set<number>([
  SendbirdErrorCode.NETWORK_ERROR,
  SendbirdErrorCode.NETWORK_ROUTING_ERROR,
  SendbirdErrorCode.CONNECTION_REQUIRED,
  SendbirdErrorCode.CONNECTION_CANCELED,
  SendbirdErrorCode.WEBSOCKET_CONNECTION_CLOSED,
  SendbirdErrorCode.WEBSOCKET_CONNECTION_FAILED,
  SendbirdErrorCode.ACK_TIMEOUT,
  SendbirdErrorCode.LOGIN_TIMEOUT,
  SendbirdErrorCode.REQUEST_FAILED,
  SendbirdErrorCode.REQUEST_CANCELED,
]);

// Message Sendbird sets on a generic network failure. Matched as a fallback for
// the rare case the thrown value carries the name/message but not a `code`.
const I18N_SKIP_NETWORK_ERROR_MESSAGE = "There was a network error.";

/**
 * Classifies whether a thrown Sendbird error is an expected transient
 * network/offline/connection condition (vs. an unexpected defect worth
 * surfacing in Error Tracking).
 *
 * The legacy app-wide channel fetch (`fetchChannels`) issues Sendbird SDK
 * requests in the background on every page as the SDK reconnects (tab focus
 * changes, flaky networks). On a dropped connection these reject with
 * `SendbirdError: There was a network error.` and, being fire-and-forget,
 * surfaced as unhandled Datadog RUM Error Tracking noise
 * (issue f6292cdc-d16a-11ee-af53-da7ad0900002).
 */
export function isExpectedSendbirdNetworkError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  if ("code" in error) {
    const { code } = error as { code?: unknown };
    if (typeof code === "number" && EXPECTED_NETWORK_ERROR_CODES.has(code)) {
      return true;
    }
  }

  return (
    error instanceof Error &&
    error.name === "SendbirdError" &&
    error.message === I18N_SKIP_NETWORK_ERROR_MESSAGE
  );
}
